import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Services.module.css';
import skilledNursing from '../assets/images/skilled-nursing.png';
import physicalTherapy from '../assets/images/physical-therapy.png';
import ocupationTherapy from '../assets/images/ocupation-therapy.png';
import speechTherapy from '../assets/images/speechTherapyImg.jpg';
import socialService from '../assets/images/social-service.png';
import demantiaHelp from '../assets/images/demantia-help.png';
import geriatricCare from '../assets/images/geriatric-care.png';
import medicationReminders from '../assets/images/medication-reminders.png';
import postHospitalizationCare from '../assets/images/post-hospitalization-care.png';
import about1 from '../assets/images/about-1.png';
import { FaUserNurse, FaWalking, FaHandHoldingHeart, FaComments, FaUsers, FaHome, FaBrain, FaClipboardList, FaPills, FaHospital } from 'react-icons/fa';
import SEO from '../components/SEO';

const Services = () => {
  const services = [
    {
      id: 1,
      title: 'Skilled Nursing',
      description: 'Registered nurses provide skilled care and instruction to patients and families, including wound care, medication supervision, catheter care, treatments and injections, and I.V. therapy.',
      image: skilledNursing,
      icon: <FaUserNurse />,
      link: '/services/skilled-nursing'
    },
    {
      id: 2,
      title: 'Physical Therapy',
      description: 'Licensed physical therapists help patients regain strength, mobility, and independence through personalized treatment plans and therapeutic exercises.',
      image: physicalTherapy,
      icon: <FaWalking />,
      link: '/services/physical-therapy'
    },
    {
      id: 3,
      title: 'Occupational Therapy',
      description: 'Occupational therapists assist patients in developing, recovering, and maintaining daily living skills for improved quality of life and independence.',
      image: ocupationTherapy,
      icon: <FaHandHoldingHeart />,
      link: '/services/occupational-therapy'
    },
    {
      id: 4,
      title: 'Speech Therapy',
      description: 'Speech-language pathologists help patients improve communication, swallowing difficulties, and cognitive skills through specialized therapy techniques.',
      image: speechTherapy,
      icon: <FaComments />,
      link: '/services/speech-therapy'
    },
    {
      id: 5,
      title: 'Social Services',
      description: 'Social workers provide counseling, community resource connections, and support for patients and families navigating healthcare challenges.',
      image: socialService,
      icon: <FaUsers />,
      link: '/services/social-services'
    },
    {
      id: 6,
      title: 'Certified Home Health Aid',
      description: 'Certified aides assist patients with personal care, meal preparation, ambulation, and exercises under professional supervision to support independent living at home.',
      image: about1,
      icon: <FaHome />,
      link: '/services/certified-home-health-aid'
    },
    {
      id: 7,
      title: 'Alzheimer\'s Care',
      description: 'Specialized care and support for patients with Alzheimer\'s disease and dementia, focusing on safety, dignity, and quality of life.',
      image: demantiaHelp,
      icon: <FaBrain />,
      link: '/services/alzheimers-care'
    },
    {
      id: 8,
      title: 'Geriatric Care Management',
      description: 'Comprehensive care coordination and advocacy services to help seniors and their families navigate complex healthcare and social service systems.',
      image: geriatricCare,
      icon: <FaClipboardList />,
      link: '/services/geriatric-care-management'
    },
    {
      id: 9,
      title: 'Medication Reminders',
      description: 'Professional medication management services to ensure proper medication adherence and prevent missed doses or dangerous interactions.',
      image: medicationReminders,
      icon: <FaPills />,
      link: '/services/medication-reminders'
    },
    {
      id: 10,
      title: 'Post-hospitalization Care',
      description: 'Transitional care services to support safe recovery at home after hospital discharge, reducing readmission risks and promoting healing.',
      image: postHospitalizationCare,
      icon: <FaHospital />,
      link: '/services/post-hospitalization-care'
    }
  ];

  return (
    <div className={styles.servicesPage}>
      <SEO
        title="Our Services"
        description="Comprehensive home health care services including skilled nursing, physical therapy, occupational therapy, speech therapy, Alzheimer's care, and more. Licensed and ACHC accredited."
        keywords="skilled nursing, physical therapy, occupational therapy, speech therapy, social services, home health aide, Alzheimer's care, geriatric care, medication management"
        canonicalUrl="https://allforyouhc.com/services"
      />
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <p className={styles.heroTag}>OUR SERVICES</p>
          <h1 className={styles.heroTitle}>
            Comprehensive Home<br />Health Care Services
          </h1>
          <p className={styles.heroDescription}>
            We provide high-quality, personalized care services designed to help patients
            maintain their independence and quality of life in the comfort of their own homes.
          </p>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className={styles.servicesSection}>
        <div className={styles.container}>
          <div className={styles.servicesGrid}>
            {services.map((service) => (
              <Link key={service.id} to={service.link} className={styles.serviceCard}>
                <div className={styles.serviceImageContainer}>
                  <img src={service.image} alt={service.title} className={styles.serviceImage} />
                  <div className={styles.serviceIconBadge}>{service.icon}</div>
                </div>
                <div className={styles.serviceContent}>
                  <h3 className={styles.serviceTitle}>{service.title}</h3>
                  <p className={styles.serviceDescription}>{service.description}</p>
                  <div className={styles.serviceLink}>
                    <span>View service</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Ready to Get Started?</h2>
            <p className={styles.ctaText}>
              Contact us today to learn more about our services and how we can help you or your
              loved one receive quality care at home.
            </p>
            <div className={styles.ctaButtons}>
              <Link to="/contact" className={styles.ctaButtonPrimary}>
                Contact Us
              </Link>
              <Link to="/about" className={styles.ctaButtonSecondary}>
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
